import { useMemo } from "react";
import cn from "classnames";
import { airStatusLabelMap } from "entities/shared/airCard.types";
import { parcelStatusLabelMap } from "entities/shared/parcelCard.types";
import { railStatusLabelMap } from "entities/shared/railCard.types";
import { roadStatusLabelMap } from "entities/shared/roadCard.types";
import { seaStatusLabelMap } from "entities/shared/seaCard.types";
import { ShipmentType, StatusProps } from "entities/shared/shipmentCard.types";
import { ValueOf } from "shared/lib/typeCheck.lib";
import { useTranslation } from "shared/localization/localization";
import { ArrowRoundedIcon } from "shared/ui/Icons/Icons";
import styles from "./CardStatus.module.scss";

import { CardHeaderStatus } from "../../Card";
import { CardContextType } from "../../Card.context";
import { Status } from "../Status";

const statusLabelMapByShipmentType: Record<ValueOf<typeof ShipmentType>, Record<string, string>> = {
  [ShipmentType.AIR]: airStatusLabelMap,
  [ShipmentType.SEA]: seaStatusLabelMap,
  [ShipmentType.RAIL]: railStatusLabelMap,
  [ShipmentType.ROAD]: roadStatusLabelMap,
  [ShipmentType.PARCEL]: parcelStatusLabelMap,
};

export type CardStatusProps = StatusProps<CardHeaderStatus> &
  Pick<CardContextType, "onUpdate" | "canUpdate" | "isUpdating"> & {
    shipmentType: ValueOf<typeof ShipmentType>;
  };

export const CardStatus: RFC<CardStatusProps> = ({
  shipmentType,
  onUpdate,
  canUpdate,
  isUpdating,
  status,
  className,
  style,
}) => {
  const { t } = useTranslation();

  const statusLabel = useMemo(() => {
    if (!status) return null;

    const labelMap = statusLabelMapByShipmentType[shipmentType];
    return labelMap?.[status] ?? null;
  }, [shipmentType, status]);

  if (canUpdate && onUpdate) {
    return (
      <button
        type="button"
        className={cn(styles.cardStatus, isUpdating && styles.isUpdating, className)}
        onClick={onUpdate}
        style={style}
        data-test-id="card-status-update-button"
      >
        <span className={styles.text}>{t("update")}</span>
        <ArrowRoundedIcon className={styles.icon} />
      </button>
    );
  }

  if (status && statusLabel) {
    return (
      <Status
        shipmentType={shipmentType}
        className={className}
        status={status}
        style={style}
        dataTestId={`card-status-${status.toLowerCase()}`}
      >
        {t(statusLabel)}
      </Status>
    );
  }

  return null;
};

CardStatus.displayName = CardStatus.name;
